import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { createBookingPropertyURL, cancellationPolicyURL, uploadBookingPropertyImagesURL } from "../../../utils/urls";

const COMMISSION_TYPE_OPTIONS = [
  { label: "Percentage", value: "percentage" },
  { label: "Fixed Amount", value: "fixed" }
];

const emptyTier = () => ({ minNights: 1, maxNights: null, type: "percentage", value: 10 });

const ListUnitForBookingDialog = ({
  visible,
  onHide,
  unit,
  facilityId,
  currencies = [],
  onSuccess
}) => {
  // State for form
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [homeOwnerInfo, setHomeOwnerInfo] = useState(null);
  const [namedCancellationPolicies, setNamedCancellationPolicies] = useState([]);
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [formData, setFormData] = useState({
    propertyName: "",
    // propertyType removed - auto-picked by backend from unit type
    description: "",
    basePrice: 0,
    currencyId: "",
    weekendPriceAdjustment: 0,
    minimumStay: 1,
    maximumStay: 30,
    advanceBookingDays: 0,
    amenities: [],
    commission: 10,
    cancellationPolicyDescription: "",
    cancellationPolicyId: ""
  });

  // State for custom amenity input
  const [customAmenityInput, setCustomAmenityInput] = useState("");
  const [customAmenities, setCustomAmenities] = useState([]);

  // Photos to show guests browsing this unit online. The property doesn't
  // exist yet at this point in the flow, so these are just staged as File
  // objects and actually uploaded right after the property is created in
  // handleSubmit - from the staff's point of view it's one single action.
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Options for dropdowns
  const propertyTypes = [
    { label: "Apartment", value: "Apartment" },
    { label: "House", value: "House" },
    { label: "Studio", value: "Studio" },
    { label: "One Bedroom", value: "One Bedroom" },
    { label: "Two Bedroom", value: "Two Bedroom" },
    { label: "Three Bedroom", value: "Three Bedroom" },
    { label: "Office", value: "Office" },
    { label: "Store", value: "Store" },
    { label: "Warehouse", value: "Warehouse" }
  ];

  const amenitiesOptions = [
    { label: "WiFi", value: "WiFi" },
    { label: "Air Conditioning", value: "Air Conditioning" },
    { label: "Heating", value: "Heating" },
    { label: "Kitchen", value: "Kitchen" },
    { label: "TV", value: "TV" },
    { label: "Parking", value: "Parking" },
    { label: "Elevator", value: "Elevator" },
    { label: "Washing Machine", value: "Washing Machine" },
    { label: "Security", value: "Security" }
  ];

  // Fetch homeowner information
  const fetchHomeOwnerInfo = async (homeOwnerId) => {
    try {
      const response = await makeRequest2(`/api/customers/${facilityId}/${homeOwnerId}`, "GET");

      if (response.success) {
        const customer = response.data?.data || response.data;
        setHomeOwnerInfo({
          name: `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
          email: customer.email || '',
          phone: customer.phoneNumber || ''
        });
      }
    } catch (error) {
      console.error("Error fetching homeowner info:", error);
      setHomeOwnerInfo({ name: "Unknown Owner", email: "", phone: "" });
    }
  };

  // Fetch the facility's named cancellation policies for assignment
  const fetchCancellationPolicies = async () => {
    try {
      const response = await makeRequest2(`${cancellationPolicyURL}/${facilityId}`, "GET");
      if (response.success) {
        const list = response.data?.data || response.data || [];
        setNamedCancellationPolicies(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error("Error fetching cancellation policies:", error);
    }
  };

  // Initialize form data when apartment or dialog opens
  useEffect(() => {
    if (unit && visible) {
      fetchCancellationPolicies();
      const defaultCurrencyId = currencies.length > 0 ? currencies[0]._id : "";

      setFormData({
        propertyName: unit.name || "",
        // propertyType auto-picked by backend
        description: "",
        basePrice: 0,
        currencyId: defaultCurrencyId,
        weekendPriceAdjustment: 0,
        minimumStay: 1,
        maximumStay: 30,
        advanceBookingDays: 0,
        amenities: [],
        commission: 10,
        cancellationPolicyDescription: "",
        cancellationPolicyId: ""
      });

      // Reset other states
      setCommissionTiers([]);
      setCustomAmenities([]);
      setCustomAmenityInput("");
      setError(null);
      setHomeOwnerInfo(null);
      setSelectedImageFiles([]);

      // Fetch homeowner information if available
      if (unit.homeOwnerId) {
        fetchHomeOwnerInfo(unit.homeOwnerId);
      }
    }
  }, [unit, visible, currencies]);

  const handleAddCommissionTier = () => {
    setCommissionTiers((prev) => [...prev, emptyTier()]);
  };

  const handleRemoveCommissionTier = (index) => {
    setCommissionTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCommissionTierChange = (index, field, value) => {
    setCommissionTiers((prev) =>
      prev.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier))
    );
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Get selected currency symbol
  const getSelectedCurrencySymbol = () => {
    const selectedCurrency = currencies.find(c => c._id === formData.currencyId);
    return selectedCurrency ? (selectedCurrency.symbol || selectedCurrency.code) : '';
  };

  // Handle adding a custom amenity
  const handleAddCustomAmenity = () => {
    if (!customAmenityInput.trim()) {
      return;
    }

    const isExistingOption = amenitiesOptions.some(
      option => option.value.toLowerCase() === customAmenityInput.trim().toLowerCase()
    );

    const isDuplicate = customAmenities.includes(customAmenityInput.trim());

    if (isExistingOption) {
      toastify("This amenity already exists in the predefined options", "info");
      return;
    }

    if (isDuplicate) {
      toastify("You've already added this custom amenity", "info");
      return;
    }

    setCustomAmenities([...customAmenities, customAmenityInput.trim()]);
    setCustomAmenityInput("");
  };

  // Handle removing a custom amenity
  const handleRemoveCustomAmenity = (amenity) => {
    setCustomAmenities(customAmenities.filter(a => a !== amenity));
  };

  const handleImageFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedImageFiles((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const handleRemoveStagedImage = (index) => {
    setSelectedImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!unit) {
        const errorMsg = "No apartment selected for listing";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      const requiredFields = ['propertyName', 'basePrice', 'currencyId']; // propertyType is auto-picked by backend

      const missingFields = requiredFields.filter(field => !formData[field]);

      if (missingFields.length > 0) {
        const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      if (!currencies.some(curr => curr._id === formData.currencyId)) {
        setError("Please select a valid currency");
        toastify("Please select a valid currency", "error");
        return;
      }

      const invalidTier = commissionTiers.find(
        (tier) => !tier.minNights || tier.minNights < 1 || tier.value === null || tier.value === undefined || tier.value < 0
      );
      if (invalidTier) {
        setError("Each commission tier needs a valid minimum nights and value");
        toastify("Each commission tier needs a valid minimum nights and value", "error");
        return;
      }

      const combinedAmenities = [
        ...formData.amenities,
        ...customAmenities
      ];

      const payload = {
        facilityId,
        unitId: unit._id,
        propertyName: formData.propertyName,
        // propertyType is auto-picked by backend from unit type
        description: formData.description,
        amenities: combinedAmenities,
        isListed: true,
        basePrice: formData.basePrice,
        currencyId: formData.currencyId,
        weekendPriceAdjustment: formData.weekendPriceAdjustment,
        minimumStay: formData.minimumStay,
        maximumStay: formData.maximumStay,
        advanceBookingDays: formData.advanceBookingDays,
        commission: formData.commission,
        commissionTiers
      };

      if (formData.cancellationPolicyId) {
        payload.cancellationPolicyId = formData.cancellationPolicyId;
      }

      // Add cancellation policy details if provided
      if (formData.cancellationPolicyDescription) {
        payload.cancellationPolicyDetails = {
          description: formData.cancellationPolicyDescription
        };
      }

      const response = await makeRequest2(
        `${createBookingPropertyURL}/${facilityId}`,
        "POST",
        payload
      );

      if (response.success || (response.error && response.error.includes("Error: 201"))) {
        toastify("Apartment successfully listed", "success");

        // Listing a unit is not a reservation event; avoid skewing reservation trends.

        let createdBookingProperty = null;
        if (response.success && response.data) {
          createdBookingProperty = response.data?.data || response.data;
        }

        // The property only exists now - upload any photos staged earlier
        // in this same submission, so listing a unit and adding its photos
        // feels like one action to the person doing it.
        const newPropertyId = createdBookingProperty?._id;
        if (newPropertyId && selectedImageFiles.length > 0) {
          try {
            setUploadingImages(true);
            const imageFormData = new FormData();
            selectedImageFiles.forEach((file) => imageFormData.append("images", file));

            const uploadResponse = await makeRequest2(
              `${uploadBookingPropertyImagesURL}/${facilityId}/${newPropertyId}`,
              "POST",
              imageFormData
            );

            if (!uploadResponse.success) {
              toastify("Apartment listed, but photos failed to upload. Add them from Edit.", "warn");
            }
          } catch (uploadError) {
            console.error("Error uploading listing photos:", uploadError);
            toastify("Apartment listed, but photos failed to upload. Add them from Edit.", "warn");
          } finally {
            setUploadingImages(false);
          }
        }

        if (onSuccess) {
          onSuccess(createdBookingProperty);
        }

        onHide();
      } else {
        throw new Error(response.error || "Failed to list apartment for booking");
      }
    } catch (error) {
      console.error("Error listing apartment for booking:", error);
      setError(error.message || "Failed to list apartment for booking");
      toastify("Failed to list apartment for booking", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!unit) {
    return null;
  }

  return (
    <Dialog
      header={`List Apartment for Booking - ${unit.name}`}
      visible={visible}
      style={{ width: '650px' }}
      onHide={onHide}
      footer={
        <div className="d-flex justify-content-end gap-2">
          <Button label="Cancel" icon="ti ti-x" className="p-button-text" onClick={onHide} />
          <Button
            label={uploadingImages ? "Uploading photos..." : "List Apartment"}
            icon="ti ti-check"
            className="p-button-primary"
            onClick={handleSubmit}
            loading={loading || uploadingImages}
          />
        </div>
      }
    >
      {error && (
        <div className="alert alert-danger mb-3">
          <p className="mb-0">{error}</p>
        </div>
      )}

      <div className="p-fluid">
        <div className="row">
          {/* Selected apartment Info */}
          <div className="col-12 mb-4">
            <div className="card border">
              <div className="card-body p-3">
                <h6 className="card-title mb-2">
                  <i className="ti ti-building me-2 text-primary"></i>
                  {unit.name}
                </h6>
                <div className="row text-sm">
                  <div className="col-md-6">
                    <p className="mb-1"><strong>Type:</strong> {unit.unitType}</p>
                    <p className="mb-1"><strong>Division:</strong> {unit.division}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1"><strong>Floor:</strong> {unit.floorUnitNo}</p>
                    {homeOwnerInfo && (
                      <p className="mb-1"><strong>Owner:</strong> {homeOwnerInfo.name}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cancellation Policy Assignment */}
          <div className="col-12 mb-4">
            <label htmlFor="cancellationPolicyId" className="form-label">Cancellation Policy</label>
            <Dropdown
              id="cancellationPolicyId"
              value={formData.cancellationPolicyId}
              options={namedCancellationPolicies.map((p) => ({ label: p.name, value: p._id }))}
              onChange={(e) => handleFormChange('cancellationPolicyId', e.value)}
              className="w-100"
              placeholder="Select a cancellation policy"
              showClear
            />
            <small className="d-block text-muted mt-1">
              Manage named policies from Booking Management &gt; Booking Settings &gt; Cancellation Policies.
            </small>
          </div>

          {/* Basic Information */}
          <div className="col-12">
            <h6 className="mb-3">
              <i className="ti ti-info-circle me-2 text-info"></i>
              Basic Information
            </h6>
            <Divider className="mb-4" />
          </div>

          {/* Photos shown to guests browsing this unit online */}
          <div className="col-12 mb-4">
            <label className="form-label">
              <i className="ti ti-photo me-2 text-warning"></i>
              Photos
            </label>
            <small className="d-block text-muted mb-2">
              These are the photos guests will see for this unit on the booking widget/WordPress site.
            </small>

            {selectedImageFiles.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                {selectedImageFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="position-relative" style={{ width: "120px" }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      className="w-100 rounded border"
                      style={{ height: "90px", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0"
                      style={{ width: "22px", height: "22px", lineHeight: "1" }}
                      onClick={() => handleRemoveStagedImage(index)}
                      title="Remove photo"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageFilesSelected}
              disabled={loading || uploadingImages}
              className="form-control"
            />
            <small className="text-muted d-block mt-1">
              Photos are uploaded once this apartment is listed below.
            </small>
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="propertyName" className="form-label">Property Name *</label>
            <InputText
              id="propertyName"
              value={formData.propertyName}
              onChange={(e) => handleFormChange('propertyName', e.target.value)}
              className="w-100"
              placeholder="Enter property display name"
            />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="propertyType" className="form-label">Property Type (Auto-selected)</label>
            <InputText
              id="propertyType"
              value={unit.unitType || 'N/A'}
              disabled
              className="w-100"
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
          </div>

          <div className="col-12 mb-3">
            <label htmlFor="description" className="form-label">Description</label>
            <InputText
              id="description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              className="w-100"
              placeholder="Brief description of the property"
            />
          </div>

          <div className="col-12 mb-3">
            <div className="d-flex align-items-center mb-2">
              <label htmlFor="amenities" className="form-label mb-0">Standard Amenities</label>
              <i
                className="ti ti-info-circle ms-2 text-info"
                style={{ fontSize: '1.1rem', cursor: 'help' }}
                title="Select from common amenities below. You can also add custom amenities in the next section."
              />
            </div>
            <MultiSelect
              id="amenities"
              value={formData.amenities}
              options={amenitiesOptions}
              onChange={(e) => handleFormChange('amenities', e.value)}
              className="w-100"
              placeholder="Select available amenities"
              display="chip"
            />
            <small className="text-muted mt-1 d-block">
              Selected {formData.amenities.length} standard amenity{formData.amenities.length !== 1 ? 's' : ''}
            </small>
          </div>

          <div className="col-12 mb-3">
            <div className="border rounded p-3 bg-light">
              <div className="d-flex align-items-center mb-2">
                <label htmlFor="customAmenities" className="form-label mb-0 fw-semibold">
                  <i className="ti ti-plus-circle me-2 text-primary"></i>
                  Add Custom Amenities
                </label>
              </div>
              <small className="text-muted d-block mb-3">
                Can't find what you need? Add your own custom amenities here (e.g., "Pool", "Gym", "Garden")
              </small>
              <div className="d-flex mb-2">
                <InputText
                  id="customAmenities"
                  value={customAmenityInput}
                  onChange={(e) => setCustomAmenityInput(e.target.value)}
                  placeholder="Type custom amenity name and press Enter or click +"
                  className="flex-grow-1 me-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomAmenity();
                    }
                  }}
                />
                <Button
                  icon="ti ti-plus"
                  onClick={handleAddCustomAmenity}
                  className="p-button-primary"
                  tooltip="Add custom amenity"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>

              {customAmenities.length > 0 && (
                <div className="custom-amenities-container mt-3 p-2 bg-white rounded border">
                  <small className="text-muted d-block mb-2 fw-semibold">Custom Amenities Added:</small>
                  {customAmenities.map((amenity, index) => (
                    <Chip
                      key={index}
                      label={amenity}
                      removable
                      onRemove={() => handleRemoveCustomAmenity(amenity)}
                      className="me-2 mb-2"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pricing & Policies */}
          <div className="col-12">
            <h6 className="mb-3 mt-4">
              <i className="ti ti-currency-dollar me-2 text-success"></i>
              Pricing & Policies
            </h6>
            <Divider className="mb-4" />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="basePrice" className="form-label">Base Price *</label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">{getSelectedCurrencySymbol()}</span>
              <InputNumber
                id="basePrice"
                value={formData.basePrice}
                onValueChange={(e) => handleFormChange('basePrice', e.value)}
                min={0}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="currencyId" className="form-label">Currency *</label>
            <Dropdown
              id="currencyId"
              value={formData.currencyId}
              options={currencies.map(c => ({ label: `${c.name} (${c.code})`, value: c._id }))}
              onChange={(e) => handleFormChange('currencyId', e.value)}
              className="w-100"
              placeholder="Select currency"
            />
            {currencies.length === 0 && (
              <small className="text-danger">
                No currencies available. Please contact your administrator.
              </small>
            )}
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="weekendPriceAdjustment" className="form-label">Weekend Adjustment (%)</label>
            <InputNumber
              id="weekendPriceAdjustment"
              value={formData.weekendPriceAdjustment}
              onValueChange={(e) => handleFormChange('weekendPriceAdjustment', e.value)}
              className="w-100"
              suffix="%"
              min={-100}
              max={100}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="minimumStay" className="form-label">Minimum Stay (Days)</label>
            <InputNumber
              id="minimumStay"
              value={formData.minimumStay}
              onValueChange={(e) => handleFormChange('minimumStay', e.value)}
              className="w-100"
              min={1}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="maximumStay" className="form-label">Maximum Stay (Days)</label>
            <InputNumber
              id="maximumStay"
              value={formData.maximumStay}
              onValueChange={(e) => handleFormChange('maximumStay', e.value)}
              className="w-100"
              min={1}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="advanceBookingDays" className="form-label">Advance Booking (Days)</label>
            <InputNumber
              id="advanceBookingDays"
              value={formData.advanceBookingDays}
              onValueChange={(e) => handleFormChange('advanceBookingDays', e.value)}
              className="w-100"
              min={0}
            />
          </div>

          <div className="col-12 mb-3">
            <label htmlFor="cancellationPolicyDescription" className="form-label">
              Custom Cancellation Policy Details (Optional)
            </label>
            <InputTextarea
              id="cancellationPolicyDescription"
              value={formData.cancellationPolicyDescription}
              onChange={(e) => handleFormChange('cancellationPolicyDescription', e.target.value)}
              className="w-100"
              rows={3}
              placeholder="Add specific cancellation terms for this property (e.g., special refund rules, processing fees, etc.)"
            />
            <small className="text-muted d-block mt-1">
              This will be shown to guests along with the standard policy terms above
            </small>
          </div>

          <div className="col-12 mb-3">
            <label htmlFor="commission" className="form-label">Default Commission (%) *</label>
            <InputNumber
              id="commission"
              value={formData.commission}
              onValueChange={(e) => handleFormChange('commission', e.value)}
              className="w-100"
              suffix="%"
              min={0}
              max={100}
            />
            <small className="text-muted d-block mt-1">
              Used whenever a stay doesn't match any tier below. Commission is internal-only and is never shown to guests.
            </small>
          </div>

          <div className="col-12 mb-3">
            <div className="border rounded p-3 bg-light">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0 fw-semibold">
                  <i className="ti ti-stairs me-2 text-primary"></i>
                  Commission Tiers by Length of Stay (Optional)
                </label>
                <Button
                  label="Add Tier"
                  icon="ti ti-plus"
                  className="p-button-sm p-button-outlined"
                  onClick={handleAddCommissionTier}
                />
              </div>
              <small className="text-muted d-block mb-3">
                E.g. 1-2 nights at 5%, 3-6 nights at 10%, 7+ nights at 20% - or a fixed currency amount per tier.
                Internal only, never shown to the guest.
              </small>

              {commissionTiers.length === 0 ? (
                <div className="text-muted small">No tiers added - the default commission above will always apply.</div>
              ) : (
                commissionTiers.map((tier, index) => (
                  <div className="row align-items-end mb-2" key={index}>
                    <div className="col-3">
                      <label className="form-label small mb-1">Min Nights</label>
                      <InputNumber
                        value={tier.minNights}
                        onValueChange={(e) => handleCommissionTierChange(index, 'minNights', e.value)}
                        className="w-100"
                        min={1}
                      />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-1">Max Nights</label>
                      <InputNumber
                        value={tier.maxNights}
                        onValueChange={(e) => handleCommissionTierChange(index, 'maxNights', e.value)}
                        className="w-100"
                        min={tier.minNights || 1}
                        placeholder="No limit"
                      />
                    </div>
                    <div className="col-3">
                      <label className="form-label small mb-1">Type</label>
                      <Dropdown
                        value={tier.type}
                        options={COMMISSION_TYPE_OPTIONS}
                        onChange={(e) => handleCommissionTierChange(index, 'type', e.value)}
                        className="w-100"
                      />
                    </div>
                    <div className="col-2">
                      <label className="form-label small mb-1">Value</label>
                      <InputNumber
                        value={tier.value}
                        onValueChange={(e) => handleCommissionTierChange(index, 'value', e.value)}
                        className="w-100"
                        min={0}
                        suffix={tier.type === 'percentage' ? '%' : undefined}
                      />
                    </div>
                    <div className="col-1">
                      <Button
                        icon="ti ti-trash"
                        className="p-button-danger p-button-sm"
                        onClick={() => handleRemoveCommissionTier(index)}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ListUnitForBookingDialog;
